// J2 post an item · J3 ask about an item (private) · the feed and item page around them.

import type { Claim } from '@model/index.ts';
import { html } from '../html.ts';
import { t } from '../i18n.ts';
import { flash, go, local, on, onForm, refuse } from '../core.ts';
import type { Screen } from '../core.ts';
import { world as w } from '../world.ts';
import {
  acceptOf,
  activePause,
  appealOf,
  isAppealDecision,
  repliesTo,
  canSee,
  claim,
  endedPause,
  feed,
  inquiries,
  inquiryBy,
  isMember,
  myLive,
  num,
  offerState,
  otherParty,
  str,
  thread,
} from '../queries.ts';
import { explain, offerGate } from '../explain.ts';
import { back, blockOf, icon, itemRow, linkButton, listen, monogram, personHref, tradesLine, whyCard } from '../ui.ts';
import { itemArt, kindArt, PICKABLE } from '../art.ts';
import { fmtAgo, fmtDate, fmtDateTime, fmtPrice } from '../format.ts';

export function feedScreen(): Screen {
  const items = feed(w, w.me.id);
  return {
    title: t('feed.title'),
    tab: 'nearby',
    body: html`
      <h1 tabindex="-1">${t('feed.title')}</h1>
      ${items.length ? html`<ul class="cards">${items.map((o) => itemRow(o))}</ul>` : html`<p class="muted">${t('feed.empty')}</p>`}
    `,
  };
}

export function itemScreen(id: string): Screen {
  const o = claim(w, id);
  const me = w.me.id;
  if (!o || o.predicate !== 'offer') return { title: '', tab: 'nearby', body: html`${back('#/')}<p>Not found.</p>` };
  const state = offerState(w, o.id);
  const mine = o.author === me;
  const deal = acceptOf(w, o.id);
  const party = deal && (deal.author === me || mine);
  const ask = inquiryBy(w, me, o.id);
  const seller = w.name(o.author);
  const paused = activePause(w, o.author, me);
  return {
    title: str(o, 'title'),
    tab: 'nearby',
    body: html`
      ${back('#/')}
      ${itemArt(str(o, 'title'), 'hero', str(o, 'kind'))}
      <h1 tabindex="-1" class="item-h">${str(o, 'title')}</h1>
      <p class="price-lg">${fmtPrice(num(o, 'priceInr'))}</p>
      <p class="item-when">${[blockOf(o.author), fmtAgo(o.issuedAt, w.now())].filter(Boolean).join(' · ')}</p>
      ${str(o, 'details') ? html`<p class="details">${str(o, 'details')}</p>` : ''}
      <div class="seller">
        ${monogram(o.author)}
        <span class="seller-text">
          <span class="seller-by">${t('item.seller')} <a href="${personHref(o.author)}">${seller}</a></span>
          <span class="seller-standing">${tradesLine(o.author)}</span>
        </span>
      </div>
      ${state === 'agreed'
        ? html`<p class="note">${t('item.agreed')}</p>${party && deal ? linkButton(t('item.openTrade'), `#/trade/${deal.id}`) : ''}`
        : state === 'down'
          ? html`<p class="note">${t('item.down')}</p>`
          : mine
            ? html`<p class="note">${t('item.yours')}</p>
                <button class="quiet" type="button" data-act="withdraw" data-id="${o.id}">${t('item.takeDown')}</button>`
            : paused
              ? html`<p class="note">${t('standing.paused', { date: fmtDate(num(paused, 'until')) })}</p>`
              : html`
                  ${ask
                    ? linkButton(t('item.openChat', { name: seller }), `#/chat/${ask.id}`)
                    : linkButton(t('item.ask', { name: seller }), `#/ask/${o.id}`)}
                  <p class="assure">${icon('check')}<span>${t('item.pay')}</span></p>
                `}
    `,
  };
}

// J2: the capability gate is asked before the form is shown. A denial names its evidence.
export function sellScreen(): Screen {
  const me = w.me;
  const d = w.would(me.key, { predicate: 'offer', body: { title: '', priceInr: 0 } });
  const live = myLive(w, me.id);
  const head = html`<h1 tabindex="-1">${t('sell.title')}</h1>`;
  if (!d.admitted) {
    const why = explain(w, 'offer', d, me.id);
    w.noteCheck(me.id, 'offer', d, why.summary);
    if (why.decision) return { title: t('sell.title'), tab: 'sell', body: html`${head}${pausedCard(why.decision)}${yours(live)}` };
    return {
      title: t('sell.title'),
      tab: 'sell',
      body: html`${head}${whyCard(why, t('why.cantPost'), { takeDown: true })}`,
    };
  }
  const gate = offerGate(w, d);
  const ended = endedPause(w, me.id);
  return {
    title: t('sell.title'),
    tab: 'sell',
    body: html`
      ${head}
      ${ended ? html`<p class="note">${t('paused.ended', { date: fmtDate(num(ended, 'until')) })}</p>` : ''}
      <p class="lead" data-testid="capacity">${t('sell.count', { count: gate.used, limit: gate.limit })}</p>
      <form class="stack" data-form="offer">
        <label class="field">
          <span>${t('sell.what', { g: me.g })}</span>
          <input name="title" required maxlength="60" autocomplete="off" />
        </label>
        <fieldset class="kinds">
          <legend>${t('sell.kind')} <small>(${t('common.optional')})</small></legend>
          <div class="kind-grid">
            ${PICKABLE.map(
              (k) => html`<label class="kind"><input type="radio" name="kind" value="${k}" />${kindArt(k)}<span>${t(`kind.${k}`)}</span></label>`,
            )}
          </div>
        </fieldset>
        <label class="field">
          <span>${t('sell.price')}</span>
          <span class="money"><span class="money-sign" aria-hidden="true">₹</span><input name="price" required inputmode="numeric" pattern="[0-9,]{1,9}" autocomplete="off" data-money /></span>
          <small>${t('sell.priceHelp')}</small>
        </label>
        <label class="field">
          <span>${t('sell.details')} <small>(${t('common.optional')})</small></span>
          <textarea name="details" rows="3" maxlength="280"></textarea>
        </label>
        <button class="btn" type="submit">${t('sell.post')}</button>
      </form>
      ${yours(live)}
    `,
  };
}

function yours(live: Claim[]) {
  return html`<h2>${t('sell.yours')}</h2>
    ${live.length
      ? html`<ul class="items">
          ${live.map(
            (o) => html`<li class="item item-own">
              <a class="item-link has-art" href="#/item/${o.id}">
                ${itemArt(str(o, 'title'), 'thumb', str(o, 'kind'))}
                <span class="item-title">${str(o, 'title')}</span>
                <span class="item-price">${fmtPrice(num(o, 'priceInr'))}</span>
                <span class="item-meta">${fmtAgo(o.issuedAt, w.now())}</span>
              </a>
              <button class="quiet small" type="button" data-act="withdraw" data-id="${o.id}">${t('item.takeDown')}</button>
            </li>`,
          )}
        </ul>`
      : html`<p class="muted">${t('sell.none')}</p>`}`;
}

// J10: the consequence, in the steward's own words, with its end date.
// J11: the same card is where the person answers it: a reply on the record, or one appeal to another steward.
export function pausedCard(decision: Claim) {
  const me = w.me;
  const until = num(decision, 'until');
  const title = t('paused.title', { date: fmtDate(until) });
  const afterAppeal = isAppealDecision(w, decision);
  const who = afterAppeal ? t('paused.whoAppeal', { steward: w.name(decision.author) }) : t('paused.who', { steward: w.name(decision.author) });
  const reason = str(decision, 'note');
  const still = t('paused.still', { date: fmtDate(until) });
  const reply = repliesTo(w, decision.id, me.id).find((r) => r.author === me.id);
  const appeal = appealOf(w, decision.id);
  // Both actions are offered only when the kernel would admit them (respond.once, appeal.once).
  const canReply = w.would(me.key, { predicate: 'respond', refs: [decision.id], body: { text: '-' }, audience: decision.audience }).admitted;
  const canAppeal = !afterAppeal && w.would(me.key, { predicate: 'appeal', refs: [decision.id], body: { text: '-' } }).admitted;
  return html`<section class="why" aria-labelledby="why-title" data-why="paused">
    <h2 id="why-title">${title}</h2>
    <p>${who}</p>
    ${reason ? html`<blockquote><span class="muted">${t('paused.reason')}</span><br />${reason}</blockquote>` : ''}
    <p>${t('paused.hidden')}</p>
    <p>${still}</p>
    ${reply ? html`<p class="reply" data-testid="my-reply"><span class="muted">${t('paused.yourReply')}</span> “${str(reply, 'text')}”</p>` : ''}
    ${appeal ? html`<p class="note" data-testid="appeal-sent">${t('appeal.waiting', { date: fmtDate(appeal.issuedAt) })}</p>` : ''}
    ${canReply || canAppeal
      ? html`<h3>${t('why.what')}</h3>
        <ul class="links">
          ${canReply ? html`<li><a href="#/reply/${decision.id}">${t('paused.replyCta')}</a></li>` : ''}
          ${canAppeal ? html`<li><a href="#/appeal-form/${decision.id}">${t('paused.appealCta')}</a></li>` : ''}
        </ul>`
      : ''}
    ${listen([title, who, reason, t('paused.hidden'), still].join('. '))}
  </section>`;
}

onForm('offer', async (fd, form) => {
  if (!form.reportValidity()) return;
  const title = String(fd.get('title')).trim();
  const priceInr = Number(String(fd.get('price')).replace(/[^0-9]/g, ''));
  const details = String(fd.get('details') ?? '').trim();
  const kind = String(fd.get('kind') ?? '');
  const body: Record<string, string | number> = { title, priceInr, details };
  if (kind && kind !== 'other') body.kind = kind;
  const { claim: c, d } = await w.act(w.seat, { predicate: 'offer', body });
  if (d.admitted) {
    flash(t('sell.posted'));
    go(`#/item/${c.id}`);
  } else go('#/sell');
});

on('withdraw', async (el) => {
  const { d } = await w.act(w.seat, { predicate: 'withdraw', refs: [el.dataset.id!] });
  if (d.admitted) flash(t('sell.taken'));
  go('#/sell');
});

// J3: chats. The inquiry claim is on the record for two people; the messages never are.
export function chatsScreen(): Screen {
  const me = w.me.id;
  const list = inquiries(w, me);
  return {
    title: t('chats.title'),
    tab: 'chats',
    body: html`
      <h1 tabindex="-1">${t('chats.title')}</h1>
      ${list.length
        ? html`<ul class="items">
            ${list.map((inq) => {
              const o = claim(w, inq.refs[0]);
              const other = otherParty(w, inq, me);
              const last = thread(w, me, inq.id).at(-1);
              return html`<li class="item">
                <a class="item-link has-art" href="#/chat/${inq.id}">
                  ${o ? itemArt(str(o, 'title'), 'thumb', str(o, 'kind')) : html`<span></span>`}
                  <span class="item-title">${w.name(other)}</span>
                  <span class="item-price">${o ? fmtPrice(num(o, 'priceInr')) : ''}</span>
                  <span class="item-meta">${o ? str(o, 'title') : ''}${last ? ` · ${str(last, 'text')}` : ''}</span>
                </a>
              </li>`;
            })}
          </ul>`
        : html`<p class="muted">${t('chats.empty')}</p>`}
    `,
  };
}

export function chatScreen(kind: 'ask' | 'chat', id: string): Screen {
  const me = w.me;
  const inq = kind === 'chat' ? claim(w, id) : inquiryBy(w, me.id, id);
  if (inq && !canSee(w, me.id, inq)) {
    return { title: t('chats.title'), tab: 'chats', body: html`${back('#/chats')}<p class="note" data-testid="not-yours">${t('chat.notYours')}</p>` };
  }
  const offer = claim(w, kind === 'chat' ? inq?.refs[0] : id);
  if (!offer) return { title: '', tab: 'chats', body: html`${back('#/chats')}<p>Not found.</p>` };
  const other = inq ? otherParty(w, inq, me.id) : offer.author;
  const msgs = inq ? thread(w, me.id, inq.id) : [];
  const buyer = offer.author !== me.id;
  const live = offerState(w, offer.id) === 'live';
  const canAccept = buyer && live && isMember(w, me.id);
  const deal = acceptOf(w, offer.id);
  const myDeal = deal && (deal.author === me.id || offer.author === me.id);
  const price = fmtPrice(num(offer, 'priceInr'));
  return {
    title: w.name(other),
    tab: 'chats',
    body: html`
      ${back(kind === 'ask' ? `#/item/${offer.id}` : '#/chats')}
      <h1 tabindex="-1">${w.name(other)}</h1>
      <p class="about"><a href="#/item/${offer.id}">${str(offer, 'title')}</a> · ${price}</p>
      <p class="private">${icon('lock')}<span>${t('chat.private', { name: w.name(other) })}</span></p>
      <ol class="thread" aria-live="polite">
        ${msgs.map(
          (m) => html`<li class="${m.author === me.id ? 'mine' : 'theirs'}">
            <p>${str(m, 'text')}</p>
            <time datetime="${new Date(m.issuedAt).toISOString()}">${fmtDateTime(m.issuedAt)}</time>
          </li>`,
        )}
      </ol>
      <form class="composer" data-form="say" data-offer="${offer.id}" data-inquiry="${inq?.id ?? ''}" data-to="${other}">
        <label class="sr-only" for="msg">${t('chat.write')}</label>
        <textarea id="msg" name="text" rows="2" required maxlength="500" placeholder="${t('chat.write')}">${msgs.length ? '' : t('chat.suggest')}</textarea>
        <button class="btn btn-send" type="submit">${t('chat.send')}</button>
      </form>
      ${canAccept ? html`<button class="btn" type="button" data-act="agree" data-id="${offer.id}">${t('chat.agree', { price })}</button>` : ''}
      ${myDeal && deal ? linkButton(t('item.openTrade'), `#/trade/${deal.id}`) : ''}
      ${inq ? html`<p class="report-link"><a class="quiet" href="#/report/${w.byId.get(other)?.key}">${t('person.report', { name: w.name(other) })}</a></p>` : ''}
    `,
  };
}

onForm('say', async (fd, form) => {
  const text = String(fd.get('text') ?? '').trim();
  if (!text) return;
  let inquiry = form.dataset.inquiry;
  const offer = form.dataset.offer!;
  const me = w.me;
  if (!inquiry) {
    const seller = claim(w, offer)!.author;
    const { claim: c, d } = await w.act(me.key, { predicate: 'inquire', refs: [offer], audience: [me.id, seller] });
    if (!d.admitted) {
      refuse('inquire', t('why.inquire'), d);
      return go(`#/item/${offer}`);
    }
    inquiry = c.id;
  }
  await w.message(me.key, form.dataset.to!, inquiry, text);
  go(`#/chat/${inquiry}`);
});


// ₹ with Indian digit grouping as the seller types (3000 → 3,000; 120000 → 1,20,000). The submit handler reads digits only.
document.addEventListener('input', (e) => {
  const el = e.target;
  if (!(el instanceof HTMLInputElement) || !el.hasAttribute('data-money')) return;
  const digits = el.value.replace(/[^0-9]/g, '').slice(0, 7);
  el.value = digits ? new Intl.NumberFormat('en-IN').format(Number(digits)) : '';
});
